import React from 'react';
import { 
  Box, 
  Typography, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Divider, 
  Paper, 
  useTheme, 
  Button,
  Card,
  CardContent,
  Chip,
  Fade
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  QuestionAnswer as QuestionIcon
} from '@mui/icons-material';
import MyNote from './MyNote';
import { Viewer } from '@toast-ui/react-editor';
// supabase import 필요시 아래 경로 사용
// import { supabase } from '../../../lib/supabaseClient';

const CHOICE_MAP = [
  { label: 'A', value: 1 },
  { label: 'B', value: 2 },
  { label: 'C', value: 3 },
  { label: 'D', value: 4 },
];

export default function QuestionCard({ 
    question, 
    userAnswer, 
    onAnswerSelect, 
    showExplanation,
    onShowExplanationClick,
    correctAnswer,
    isStudyMode,
    isSubmitted
}) {
  const theme = useTheme();

  // 피드백을 보여줄지 결정하는 변수
  const shouldShowFeedback = isStudyMode ? !!userAnswer : isSubmitted;

  const getChoiceStyle = (choiceValue) => {
    if (!shouldShowFeedback) return {}; // 피드백을 보여줄 때가 아니면 아무 스타일도 적용하지 않음

    const isCorrect = choiceValue === correctAnswer;
    const isSelected = choiceValue === userAnswer;

    if (isCorrect) {
      return { 
        color: theme.palette.success.main, 
        fontWeight: 'bold',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderRadius: '8px',
        padding: '8px 12px'
      };
    }
    if (isSelected && !isCorrect) {
      return { 
        color: theme.palette.error.main,
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        borderRadius: '8px',
        padding: '8px 12px'
      };
    }
    return {};
  };

  const getChoiceCardStyle = (choiceValue) => {
    const isSelected = choiceValue === userAnswer;
    const isCorrect = choiceValue === correctAnswer;
    
    if (!shouldShowFeedback) {
      return {
        border: isSelected ? '2px solid #2196F3' : '1px solid #e0e0e0',
        backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.05)' : 'white',
        '&:hover': {
          backgroundColor: 'rgba(33, 150, 243, 0.02)',
          borderColor: '#2196F3'
        }
      };
    }

    if (isCorrect) {
      return {
        border: '2px solid #4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        '&:hover': {
          backgroundColor: 'rgba(76, 175, 80, 0.15)'
        }
      };
    }
    
    if (isSelected && !isCorrect) {
      return {
        border: '2px solid #f44336',
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        '&:hover': {
          backgroundColor: 'rgba(244, 67, 54, 0.15)'
        }
      };
    }

    return {
      border: '1px solid #e0e0e0',
      backgroundColor: 'white'
    };
  };

  const renderChoiceLabel = (label, choiceValue) => {
    const style = getChoiceStyle(choiceValue);
    const isSelected = choiceValue === userAnswer;
    const isCorrect = choiceValue === correctAnswer;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            minHeight: '24px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: shouldShowFeedback && isCorrect ? '#4CAF50' : 
                           shouldShowFeedback && isSelected && !isCorrect ? '#f44336' : 
                           isSelected ? '#2196F3' : '#e0e0e0',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0
          }}
        >
          {label}
        </Box>
        <Typography 
          sx={{ 
            ...style, 
            flex: 1,
            fontSize: '1rem',
            lineHeight: 1.6
          }}
        >
          {question[`choice_${choiceValue}`]}
        </Typography>
        {shouldShowFeedback && isCorrect && (
          <CheckCircleIcon sx={{ color: 'success.main', ml: 1, fontSize: 20 }} />
        )}
        {shouldShowFeedback && isSelected && !isCorrect && (
          <CancelIcon sx={{ color: 'error.main', ml: 1, fontSize: 20 }} />
        )}
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Card 
        sx={{ 
          borderRadius: 3, 
          boxShadow: 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* 문제 헤더 */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <QuestionIcon sx={{ fontSize: 28, color: 'primary.main', mr: 2 }} />
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              문제
            </Typography>
            {userAnswer && (
              <Chip 
                label="답변 완료"
                color="success"
                size="small"
                sx={{ ml: 'auto', fontWeight: 'bold' }}
              />
            )}
          </Box>

          {/* 문제 텍스트 */}
          <Box 
            sx={{ 
              p: 3,
              backgroundColor: 'rgba(63, 81, 181, 0.02)',
              borderRadius: 2,
              border: '1px solid rgba(63, 81, 181, 0.1)',
              mb: 4
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-line', 
                fontSize: '1.1rem', 
                lineHeight: 1.8,
                color: 'text.primary'
              }}
            >
              {question.question_text}
            </Typography>
          </Box>

          <Divider sx={{ my: 3, opacity: 0.3 }} />

          {/* 선택지 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 2 }}>
              선택지
            </Typography>
            <RadioGroup
              aria-labelledby="question-choices"
              name="question-choices"
              value={userAnswer || ''}
              onChange={(e) => onAnswerSelect(question.id, parseInt(e.target.value, 10))}
              disabled={isSubmitted || (isStudyMode && !!userAnswer)}
            >
              {CHOICE_MAP.map(({ label, value }) => (
                <Box key={value} sx={{ mb: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: (isSubmitted || (isStudyMode && !!userAnswer)) ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      ...getChoiceCardStyle(value)
                    }}
                    onClick={() => {
                      if (!isSubmitted && !(isStudyMode && !!userAnswer)) {
                        onAnswerSelect(question.id, value);
                      }
                    }}
                  >
                    <FormControlLabel
                      value={value}
                      control={
                        <Radio 
                          sx={{ 
                            color: shouldShowFeedback && value === correctAnswer ? 'success.main' :
                                   shouldShowFeedback && value === userAnswer && value !== correctAnswer ? 'error.main' :
                                   'primary.main'
                          }} 
                        />
                      }
                      label={renderChoiceLabel(label, value)}
                      sx={{ 
                        margin: 0,
                        width: '100%',
                        '& .MuiFormControlLabel-label': {
                          width: '100%'
                        }
                      }}
                    />
                  </Paper>
                </Box>
              ))}
            </RadioGroup>
          </Box>
          
          {/* 해설 보기 버튼 */}
          {((isStudyMode && userAnswer) || isSubmitted) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button 
                variant="contained" 
                onClick={() => onShowExplanationClick(question)}
                startIcon={<VisibilityIcon />}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderRadius: 3,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 6px 20px rgba(33, 150, 243, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(33, 150, 243, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                해설 보기
              </Button>
            </Box>
          )}

          {/* 나만의 노트 */}
          <Box sx={{ mt: 4 }}>
            <MyNote questionId={question.id} />
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
}
